import {Injectable} from '@angular/core';
import {CoreModule, LicenseManager} from 'dynamsoft-barcode-reader-bundle';
import {DynamsoftLicenceUsageCapService} from './dynamsoft-licence-usage-cap.service';
import {Store} from '@ngrx/store';
import {firstValueFrom} from 'rxjs';
import {take} from 'rxjs/operators';
import * as USER_SELECTORS from '../../user-access/user-store/user.selectors';
import {selectSelectedStore} from '../../user-access/user-stores-store/user-store.selectors';
import {IUser} from '../../user-access/models/user/user';
import {IStore} from '../../../shared/shared-models/store/store';

/**
 * Service for configuring Dynamsoft Barcode Reader Bundle.
 * The licence loads once per session. The store usage gate runs on every scan start.
 */
@Injectable({
  providedIn: 'root',
})
export class DynavsoftConfigService {
  private isInitialized = false;

  constructor(
    private readonly store: Store,
    private readonly dynamsoftLicenceUsageCapService: DynamsoftLicenceUsageCapService,
  ) {
  }

  /**
   * Check if Dynamsoft is initialized
   */
  get initialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Records today's scanner use for the signed-in user. Does not load Dynamsoft.
   */
  async claimScannerUse(): Promise<void> {
    const user: IUser = await firstValueFrom(this.store.select(USER_SELECTORS.selectUser).pipe(take(1)));
    const selectedStore: IStore = await firstValueFrom(this.store.select(selectSelectedStore).pipe(take(1)));
    const email = user?.contactDetails?.email?.trim() || user?.id || '';
    await this.dynamsoftLicenceUsageCapService.registerOrThrow(selectedStore?.storeId, email);
  }

  /**
   * Claims today's usage slot, then initialises Dynamsoft once.
   * A failed claim leaves the licence unloaded.
   *
   * @param licenseKey - The Dynamsoft license key
   */
  async initializeDynamsoft(licenseKey: string): Promise<void> {
    await this.claimScannerUse();

    if (this.isInitialized) {
      return;
    }

    try {
      await LicenseManager.initLicense(licenseKey);

      CoreModule.engineResourcePaths.rootDirectory = 'https://cdn.jsdelivr.net/npm/';

      await CoreModule.loadWasm();

      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize Dynamsoft Barcode Reader:', error);
      throw error;
    }
  }
}
